import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionState } from './use-session-state';

/**
 * Tab state that lives in the `tab` URL query param (deep-linkable; pressing
 * Back after visiting a detail page restores the tab you left from) and is
 * mirrored to sessionStorage so the tab also survives navigating away via the
 * sidebar and returning to the bare path.
 *
 * Precedence: valid URL param > session value > defaultTab. The default tab
 * keeps the URL clean — its param is removed rather than written.
 */
export function useTabState<T extends string>(
  sessionKey: string,
  validTabs: readonly T[],
  defaultTab: T
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessionTab, setSessionTab] = useSessionState<T>(sessionKey, defaultTab);

  const tabParam = searchParams.get('tab');
  const activeTab: T = (validTabs as readonly string[]).includes(tabParam ?? '')
    ? (tabParam as T)
    : validTabs.includes(sessionTab)
      ? sessionTab
      : defaultTab;

  // Keep the session mirror in sync when the tab changes through URL
  // navigation (back/forward, direct links) rather than through setActiveTab.
  useEffect(() => {
    setSessionTab(activeTab);
  }, [activeTab, setSessionTab]);

  const setActiveTab = (next: T) => {
    setSessionTab(next);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === defaultTab) params.delete('tab');
        else params.set('tab', next);
        return params;
      },
      { replace: true }
    );
  };

  return [activeTab, setActiveTab];
}
